import React, { useState } from 'react';
import { useSubjectLanguage } from '@features/user';
import {
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useStripeConnect, useStripeDashboard } from '../store/hooks';

import { StripeConnectRedirect } from './StripeConnectRedirect';

export const StripeConnectStatus: React.FC = () => {
  const {
    status,
    isLoading,
    error,
    isCreating,
    startOnboarding,
    hasStripeAccount,
    isStripeEnabled,
    refetchStatus,
  } = useStripeConnect();

  const { openDashboard, isLoading: isDashboardLoading } = useStripeDashboard();
  const { stripeConnect } = useEventTracking();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [onboardingUrl, setOnboardingUrl] = useState<string | null>(null);
  const subjectLanguage = useSubjectLanguage();
  const subjectsLanguage = subjectLanguage + 's';

  const handleConnectStripe = async () => {
    try {
      setIsRedirecting(true);
      // Track Stripe Connect initiation from status component
      stripeConnect.initiated({ source: 'stripe_status' });
      const result = await startOnboarding();
      if (result.onboarding_url) {
        setOnboardingUrl(result.onboarding_url);
      }
    } catch (err) {
      console.error('Failed to start Stripe onboarding:', err);
      // Track failure
      stripeConnect.failed({ 
        error_type: 'onboarding_start_error',
        error_message: err instanceof Error ? err.message : 'Unknown error'
      });
      setIsRedirecting(false);
    }
  };
  
  const handleOpenDashboard = async () => {
    try {
      // Track dashboard open
      stripeConnect.dashboardOpened({});
      await openDashboard();
    } catch (error) {
      console.error('Failed to open Stripe dashboard:', error);
    }
  };

  const handleRedirect = () => {
    if (onboardingUrl) {
      window.location.href = onboardingUrl;
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load Stripe Connect status. Please try again.
        </Alert>
        <Button variant="outlined" onClick={() => refetchStatus()}>
          Retry
        </Button>
      </>
    );
  }

  if (!hasStripeAccount) {
    // Show redirect UI if connecting
    if (isRedirecting && onboardingUrl) {
      return (
        <StripeConnectRedirect
          variant="inline"
          onCountdownComplete={handleRedirect}
          redirectDelay={5}
        />
      );
    }

    return (
      <>
        <Typography variant="body1" gutterBottom>
          Connect your Stripe account to start accepting payments from {subjectsLanguage}.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Once connected, you&apos;ll be able to process payments directly through the platform.
        </Typography>

        <Button
          variant="contained"
          color="primary"
          onClick={handleConnectStripe}
          disabled={isCreating || isRedirecting}
          startIcon={isCreating ? <CircularProgress size={20} /> : <AccountBalanceIcon />}
        >
          {isCreating ? 'Connecting...' : 'Connect Stripe Account'}
        </Button>
      </>
    );
  }

  // Show redirect UI if connecting (even with existing account)
  if (isRedirecting && onboardingUrl) {
    return (
      <StripeConnectRedirect
        variant="inline"
        onCountdownComplete={handleRedirect}
        redirectDelay={5}
      />
    );
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="body1">Account Status</Typography>
          <Chip
            label={isStripeEnabled ? 'Active' : 'Incomplete'}
            color={isStripeEnabled ? 'success' : 'warning'}
            icon={isStripeEnabled ? <CheckCircleIcon /> : <WarningIcon />}
            size="small"
          />
        </Box>
      </Box>

      {status && (
        <Stack direction="row" spacing={2}>
          <Alert
            severity={status.charges_enabled ? 'success' : 'warning'}
            icon={status.charges_enabled ? <CheckCircleIcon /> : <WarningIcon />}
            sx={{ flex: 1 }}
          >
            Charges {status.charges_enabled ? 'Enabled' : 'Disabled'}
          </Alert>

          <Alert
            severity={status.payouts_enabled ? 'success' : 'warning'}
            icon={status.payouts_enabled ? <CheckCircleIcon /> : <WarningIcon />}
            sx={{ flex: 1 }}
          >
            Payouts {status.payouts_enabled ? 'Enabled' : 'Disabled'}
          </Alert>
        </Stack>
      )}

      {!isStripeEnabled && (
        <Alert severity="warning">
          Your Stripe account setup is incomplete. Please complete the onboarding process.
          <Box mt={1}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleConnectStripe}
              disabled={isCreating || isRedirecting}
            >
              Continue Setup
            </Button>
          </Box>
        </Alert>
      )}

      {status?.details_submitted && !isStripeEnabled && (
        <Alert severity="info">
          Your details have been submitted and are under review by Stripe.
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center">
        {isStripeEnabled && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleOpenDashboard}
            disabled={isDashboardLoading}
            startIcon={isDashboardLoading ? <CircularProgress size={16} /> : <AccountBalanceIcon />}
          >
            {isDashboardLoading ? 'Opening...' : 'Open Stripe Dashboard'}
          </Button>
        )}
        <Box flex={1} />
        <Button variant="text" size="small" onClick={() => refetchStatus()}>
          Refresh Status
        </Button>
      </Box>
    </Stack>
  );
};
